import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical, Save, Loader2, ChevronDown, ChevronUp, Check, Users, Settings as SettingsIcon, FileQuestion } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const questionTypes = [
    { value: 'rating', label: 'Rating (1-5)' },
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'boolean', label: 'Yes/No' },
    { value: 'text', label: 'Open Text' },
];

const QuestionEditor = ({ question, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
    const [expanded, setExpanded] = useState(true);
    const [options, setOptions] = useState(
        Array.isArray(question.options) ? question.options :
            typeof question.options === 'string' ?
                (question.options.startsWith('[') ? JSON.parse(question.options) : question.options.split(',').map(o => o.trim()))
                : []
    );

    const handleOptionsChange = (newOptions) => {
        setOptions(newOptions);
        onChange({ ...question, options: newOptions });
    };

    return (
        <div className="border border-border rounded-lg bg-card overflow-hidden">
            <div
                className="p-4 bg-secondary flex items-center justify-between cursor-pointer hover:bg-muted transition-colors"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-3">
                    <GripVertical size={16} className="text-muted-foreground" />
                    <div>
                        <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded capitalize">
                            {question.question_type}
                        </span>
                        <p className="text-sm font-medium text-foreground mt-1 line-clamp-1">
                            {question.question_text || 'New Question'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {question.is_required && (
                        <span className="text-xs text-red-500 font-medium">Required</span>
                    )}
                    {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
            </div>

            {expanded && (
                <div className="p-4 space-y-4 border-t border-border">
                    {/* Question Text */}
                    <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Question Text</label>
                        <textarea
                            value={question.question_text || ''}
                            onChange={(e) => onChange({ ...question, question_text: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm resize-none"
                            placeholder="Enter your question..."
                        />
                    </div>

                    {/* Question Type */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-1">Type</label>
                            <select
                                value={question.question_type}
                                onChange={(e) => onChange({ ...question, question_type: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            >
                                {questionTypes.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={question.is_required || false}
                                    onChange={(e) => onChange({ ...question, is_required: e.target.checked })}
                                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                />
                                <span className="text-sm text-muted-foreground">Required</span>
                            </label>
                        </div>
                    </div>

                    {/* Options for Multiple Choice */}
                    {question.question_type === 'multiple_choice' && (
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-2">Options</label>
                            <div className="space-y-2">
                                {options.map((opt, idx) => (
                                    <div key={idx} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={opt}
                                            onChange={(e) => {
                                                const newOpts = [...options];
                                                newOpts[idx] = e.target.value;
                                                handleOptionsChange(newOpts);
                                            }}
                                            className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                            placeholder={`Option ${idx + 1}`}
                                        />
                                        <button
                                            onClick={() => handleOptionsChange(options.filter((_, i) => i !== idx))}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={() => handleOptionsChange([...options, ''])}
                                    className="flex items-center gap-2 text-sm text-primary hover:text-primary"
                                >
                                    <Plus size={14} /> Add Option
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                        <div className="flex items-center gap-1">
                            <button
                                onClick={onMoveUp}
                                disabled={isFirst}
                                className="p-2 text-muted-foreground hover:text-muted-foreground hover:bg-muted rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move Up"
                            >
                                <ChevronUp size={16} />
                            </button>
                            <button
                                onClick={onMoveDown}
                                disabled={isLast}
                                className="p-2 text-muted-foreground hover:text-muted-foreground hover:bg-muted rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move Down"
                            >
                                <ChevronDown size={16} />
                            </button>
                        </div>
                        <button
                            onClick={onDelete}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm"
                        >
                            <Trash2 size={14} /> Delete
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const SurveyEditorModal = ({ isOpen, onClose, survey, onSuccess }) => {
    const isEditMode = survey !== null && survey?.id;

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [questions, setQuestions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('questions');

    // Settings
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [isComplianceRequired, setIsComplianceRequired] = useState(false);
    const [triggerType, setTriggerType] = useState('immediate');

    // Assignment
    const [targetAudienceType, setTargetAudienceType] = useState('all');
    const [targetAudienceConfig, setTargetAudienceConfig] = useState({});
    const [availableRoles, setAvailableRoles] = useState([]);
    const [availableTeams, setAvailableTeams] = useState([]);
    const [availableEmployees, setAvailableEmployees] = useState([]);

    useEffect(() => {
        if (isOpen) {
            fetchAssignmentData();
            if (survey) {
                setTitle(survey.title || '');
                setDescription(survey.description || '');
                setQuestions(survey.questions || []);
                setIsAnonymous(survey.is_anonymous || false);
                setIsComplianceRequired(survey.is_compliance_required || false);
                setTriggerType(survey.trigger_type || 'immediate');
                setTargetAudienceType(survey.target_audience_type || 'all');
                setTargetAudienceConfig(survey.target_audience_config || {});
            } else {
                // Reset for new survey
                resetForm();
            }
        }
    }, [isOpen, survey]);

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setQuestions([]);
        setIsAnonymous(false);
        setIsComplianceRequired(false);
        setTriggerType('immediate');
        setTargetAudienceType('all');
        setTargetAudienceConfig({});
        setActiveTab('questions');
    };

    const fetchAssignmentData = async () => {
        try {
            const [rolesRes, teamsRes, employeesRes] = await Promise.all([
                api.get('/employees/roles'),
                api.get('/teams'),
                api.get('/employees')
            ]);
            setAvailableRoles(rolesRes.data.roles || rolesRes.data || []);
            setAvailableTeams(teamsRes.data.teams || teamsRes.data || []);
            setAvailableEmployees(employeesRes.data.employees || employeesRes.data || []);
        } catch (error) {
            console.error('Error fetching assignment data:', error);
            setAvailableRoles([]);
            setAvailableTeams([]);
            setAvailableEmployees([]);
        }
    };

    const handleSave = async () => {
        if (!title.trim()) {
            toast.error('Survey title is required');
            return;
        }

        if (questions.length === 0) {
            toast.error('Add at least one question');
            return;
        }

        setSaving(true);
        try {
            const surveyPayload = {
                title,
                description,
                questions: questions.map((q, idx) => ({
                    ...q,
                    order_index: idx,
                    options: Array.isArray(q.options) ? q.options : []
                })),
                is_anonymous: isAnonymous,
                is_compliance_required: isComplianceRequired,
                trigger_type: triggerType,
                target_audience_type: targetAudienceType,
                target_audience_config: targetAudienceConfig,
                status: 'active' // Create as active directly
            };

            let surveyId;
            if (isEditMode) {
                await api.put(`/surveys/${survey.id}`, surveyPayload);
                surveyId = survey.id;
                toast.success('Survey updated successfully!');
            } else {
                const response = await api.post('/surveys', surveyPayload);
                surveyId = response.data.id;

                // Auto-deploy to target audience
                try {
                    const deployRes = await api.post(`/surveys/${surveyId}/deploy`);
                    const deployedCount = deployRes.data.deployedCount || 0;
                    toast.success(`Survey created and deployed to ${deployedCount} employee${deployedCount !== 1 ? 's' : ''}!`);
                } catch (deployError) {
                    console.error('Deployment failed:', deployError);
                    toast.success('Survey created! Deployment will happen based on trigger settings.');
                }
            }

            onSuccess?.();
            onClose();
            resetForm();
        } catch (error) {
            console.error('Save failed:', error);
            toast.error(isEditMode ? 'Failed to update survey' : 'Failed to create survey');
        } finally {
            setSaving(false);
        }
    };

    const addQuestion = () => {
        setQuestions([...questions, {
            id: `new-${Date.now()}`,
            question_text: '',
            question_type: 'text',
            options: [],
            is_required: false,
            order_index: questions.length
        }]);
    };

    const updateQuestion = (index, updatedQuestion) => {
        const newQuestions = [...questions];
        newQuestions[index] = updatedQuestion;
        setQuestions(newQuestions);
    };

    const deleteQuestion = (index) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    const moveQuestion = (index, direction) => {
        const newQuestions = [...questions];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newQuestions.length) return;
        [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
        setQuestions(newQuestions);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* Modal */}
            <div className="relative w-full max-w-3xl h-full md:h-auto md:max-h-[90vh] bg-card rounded-none md:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-5 border-b border-border safe-area-top">
                    <div>
                        <h2 className="text-xl font-bold text-foreground">
                            {isEditMode ? 'Edit Survey' : 'Create Survey'}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {isEditMode ? 'Make changes to your survey' : 'Build your survey manually'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-muted-foreground hover:text-muted-foreground hover:bg-muted rounded-lg"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-border px-4 md:px-5 overflow-x-auto scrollbar-hide">
                    {[
                        { id: 'questions', label: 'Questions', icon: FileQuestion },
                        { id: 'settings', label: 'Settings', icon: SettingsIcon },
                        { id: 'assignment', label: 'Assignment', icon: Users }
                    ].map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-muted-foreground hover:text-muted-foreground'
                                    }`}
                            >
                                <Icon size={16} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 md:p-5">
                    {activeTab === 'questions' && (
                        <div className="space-y-4">
                            {/* Title & Description */}
                            <div className="space-y-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Survey Title</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                        placeholder="Enter survey title..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Description</label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={2}
                                        className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                                        placeholder="Describe your survey..."
                                    />
                                </div>
                            </div>

                            {/* Questions */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-semibold text-muted-foreground">Questions ({questions.length})</h3>
                                {questions.map((q, idx) => (
                                    <QuestionEditor
                                        key={q.id || idx}
                                        question={q}
                                        onChange={(updated) => updateQuestion(idx, updated)}
                                        onDelete={() => deleteQuestion(idx)}
                                        onMoveUp={() => moveQuestion(idx, 'up')}
                                        onMoveDown={() => moveQuestion(idx, 'down')}
                                        isFirst={idx === 0}
                                        isLast={idx === questions.length - 1}
                                    />
                                ))}
                                <button
                                    onClick={addQuestion}
                                    className="w-full py-3 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                                >
                                    <Plus size={18} /> Add Question
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div className="space-y-6">
                            {/* Trigger Type */}
                            <div>
                                <label className="block text-sm font-semibold text-muted-foreground mb-2">Trigger Type</label>
                                <select
                                    value={triggerType}
                                    onChange={(e) => setTriggerType(e.target.value)}
                                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="immediate">Immediate (One-time)</option>
                                    <option value="lifecycle">Lifecycle Event</option>
                                    <option value="recurring">Recurring</option>
                                    <option value="specific_date">Specific Date</option>
                                </select>
                            </div>

                            {/* Toggles */}
                            <div className="space-y-4">
                                <label className="flex items-center justify-between p-4 bg-secondary rounded-lg cursor-pointer hover:bg-muted transition-colors">
                                    <div>
                                        <p className="font-medium text-foreground">Anonymous Responses</p>
                                        <p className="text-sm text-muted-foreground">Employee identities will be hidden</p>
                                    </div>
                                    <div className={`w-12 h-6 rounded-full p-1 transition-colors ${isAnonymous ? 'bg-primary' : 'bg-muted'}`}>
                                        <div className={`w-4 h-4 rounded-full bg-card shadow transition-transform ${isAnonymous ? 'translate-x-6' : ''}`} />
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={isAnonymous}
                                        onChange={(e) => setIsAnonymous(e.target.checked)}
                                        className="sr-only"
                                    />
                                </label>

                                <label className="flex items-center justify-between p-4 bg-secondary rounded-lg cursor-pointer hover:bg-muted transition-colors">
                                    <div>
                                        <p className="font-medium text-foreground">Compliance Required</p>
                                        <p className="text-sm text-muted-foreground">Mandatory survey - skips consent</p>
                                    </div>
                                    <div className={`w-12 h-6 rounded-full p-1 transition-colors ${isComplianceRequired ? 'bg-red-500' : 'bg-muted'}`}>
                                        <div className={`w-4 h-4 rounded-full bg-card shadow transition-transform ${isComplianceRequired ? 'translate-x-6' : ''}`} />
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={isComplianceRequired}
                                        onChange={(e) => setIsComplianceRequired(e.target.checked)}
                                        className="sr-only"
                                    />
                                </label>
                            </div>
                        </div>
                    )}

                    {activeTab === 'assignment' && (
                        <div className="space-y-6">
                            {/* Target Audience Type */}
                            <div>
                                <label className="block text-sm font-semibold text-muted-foreground mb-2">
                                    Target Audience
                                </label>
                                <select
                                    value={targetAudienceType}
                                    onChange={(e) => {
                                        setTargetAudienceType(e.target.value);
                                        setTargetAudienceConfig({});
                                    }}
                                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="all">Company-Wide (All Employees)</option>
                                    <option value="role">Specific Roles</option>
                                    <option value="team">Specific Teams</option>
                                    <option value="specific_employees">Specific Employees</option>
                                </select>
                            </div>

                            {/* Role Selection */}
                            {targetAudienceType === 'role' && (
                                <div>
                                    <label className="block text-sm font-semibold text-muted-foreground mb-2">
                                        Select Roles
                                    </label>
                                    {availableRoles.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {availableRoles.map((role) => {
                                                const roleId = role.id;
                                                const roleName = role.role_name;
                                                const isSelected = (targetAudienceConfig?.role_ids || []).includes(roleId);
                                                return (
                                                    <button
                                                        key={roleId}
                                                        type="button"
                                                        onClick={() => {
                                                            const currentRoleIds = targetAudienceConfig?.role_ids || [];
                                                            const newRoleIds = isSelected
                                                                ? currentRoleIds.filter(r => r !== roleId)
                                                                : [...currentRoleIds, roleId];
                                                            setTargetAudienceConfig({ ...targetAudienceConfig, role_ids: newRoleIds });
                                                        }}
                                                        className={`p-4 border-2 rounded-lg text-left transition-all ${isSelected
                                                            ? 'border-indigo-500 bg-primary/10'
                                                            : 'border-border hover:border-primary/50'
                                                            }`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span className={`font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                                                                {roleName}
                                                            </span>
                                                            {isSelected && (
                                                                <Check size={18} className="text-primary" />
                                                            )}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground bg-secondary p-4 rounded-lg">
                                            No roles available. Create roles in Settings first.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Team Selection */}
                            {targetAudienceType === 'team' && (
                                <div>
                                    <label className="block text-sm font-semibold text-muted-foreground mb-2">
                                        Select Teams
                                    </label>
                                    {availableTeams.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {availableTeams.map((team) => {
                                                const isSelected = (targetAudienceConfig?.team_ids || []).includes(team.id);
                                                return (
                                                    <button
                                                        key={team.id}
                                                        type="button"
                                                        onClick={() => {
                                                            const currentTeamIds = targetAudienceConfig?.team_ids || [];
                                                            const newTeamIds = isSelected
                                                                ? currentTeamIds.filter(t => t !== team.id)
                                                                : [...currentTeamIds, team.id];
                                                            setTargetAudienceConfig({ ...targetAudienceConfig, team_ids: newTeamIds });
                                                        }}
                                                        className={`p-4 border-2 rounded-lg text-left transition-all ${isSelected
                                                            ? 'border-indigo-500 bg-primary/10'
                                                            : 'border-border hover:border-primary/50'
                                                            }`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span className={`font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                                                                {team.team_name}
                                                            </span>
                                                            {isSelected && (
                                                                <Check size={18} className="text-primary" />
                                                            )}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground bg-secondary p-4 rounded-lg">
                                            No teams available. Create teams in Settings first.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Specific Employees Selection */}
                            {targetAudienceType === 'specific_employees' && (
                                <div>
                                    <label className="block text-sm font-semibold text-muted-foreground mb-2">
                                        Select Employees
                                    </label>
                                    {availableEmployees.length > 0 ? (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                                                    {(targetAudienceConfig?.employee_ids || []).length} selected
                                                </span>
                                            </div>
                                            <div className="max-h-80 overflow-y-auto border border-border rounded-lg divide-y divide-gray-100">
                                                {availableEmployees.map((employee) => {
                                                    const isSelected = (targetAudienceConfig?.employee_ids || []).includes(employee.id);
                                                    return (
                                                        <button
                                                            key={employee.id}
                                                            type="button"
                                                            onClick={() => {
                                                                const currentEmployeeIds = targetAudienceConfig?.employee_ids || [];
                                                                const newEmployeeIds = isSelected
                                                                    ? currentEmployeeIds.filter(e => e !== employee.id)
                                                                    : [...currentEmployeeIds, employee.id];
                                                                setTargetAudienceConfig({ ...targetAudienceConfig, employee_ids: newEmployeeIds });
                                                            }}
                                                            className={`w-full p-4 text-left transition-all flex items-center gap-3 ${isSelected
                                                                ? 'bg-primary/10'
                                                                : 'hover:bg-secondary'
                                                                }`}
                                                        >
                                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${isSelected
                                                                ? 'bg-primary text-white'
                                                                : 'bg-muted text-muted-foreground'
                                                                }`}>
                                                                {employee.first_name?.[0]}{employee.last_name?.[0]}
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className={`font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                                                                    {employee.first_name} {employee.last_name}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">{employee.email}</p>
                                                                {employee.position && (
                                                                    <p className="text-xs text-muted-foreground">{employee.position}</p>
                                                                )}
                                                            </div>
                                                            {isSelected && (
                                                                <Check size={20} className="text-primary" />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground bg-secondary p-4 rounded-lg">
                                            No employees available.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Assignment Summary */}
                            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-primary mb-2">Assignment Summary</h4>
                                <div className="text-sm text-primary">
                                    {targetAudienceType === 'all' && (
                                        <p>This survey will be sent to <strong>all employees</strong> in your company.</p>
                                    )}
                                    {targetAudienceType === 'role' && (
                                        <p>
                                            This survey will be sent to employees in{' '}
                                            <strong>{(targetAudienceConfig?.role_ids || []).length} selected role(s)</strong>.
                                        </p>
                                    )}
                                    {targetAudienceType === 'team' && (
                                        <p>
                                            This survey will be sent to employees in{' '}
                                            <strong>{(targetAudienceConfig?.team_ids || []).length} selected team(s)</strong>.
                                        </p>
                                    )}
                                    {targetAudienceType === 'specific_employees' && (
                                        <p>
                                            This survey will be sent to{' '}
                                            <strong>{(targetAudienceConfig?.employee_ids || []).length} specific employee(s)</strong>.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-4 md:p-5 border-t border-border bg-secondary safe-area-bottom">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Survey')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SurveyEditorModal;
